import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { cn } from '@repo/ui/lib/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';

export type SettingsSecurityPasskeyTableActionsProps = {
  className?: string;
  passkeyId: string;
  passkeyName: string;
};

const ZUpdatePasskeySchema = z.object({
  name: z.string(),
});

type TUpdatePasskeySchema = z.infer<typeof ZUpdatePasskeySchema>;

export const SettingsSecurityPasskeyTableActions = ({
  className,
  passkeyId,
  passkeyName,
}: SettingsSecurityPasskeyTableActionsProps) => {
  useLocale();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  const form = useForm<TUpdatePasskeySchema>({
    resolver: zodResolver(ZUpdatePasskeySchema, { error: validationError }),
    defaultValues: {
      name: passkeyName,
    },
  });

  const { mutateAsync: updatePasskey, isPending: isUpdatingPasskey } =
    trpc.auth.updatePasskey.useMutation({
      onSuccess: () => {
        toast.success(t('workspace.success'), {
          get description() {
            return t('workspace.passkeyHasBeenUpdated');
          },
        });

        setIsUpdateDialogOpen(false);
      },
      onError: () => {
        toast.error(t('auth.somethingWentWrong'), {
          get description() {
            return t('workspace.weAreUnableToUpdateThisPasskeyAtThe');
          },
        });
      },
    });

  const { mutateAsync: deletePasskey, isPending: isDeletingPasskey } =
    trpc.auth.deletePasskey.useMutation({
      onSuccess: () => {
        toast.success(t('workspace.success'), {
          get description() {
            return t('workspace.passkeyHasBeenRemoved');
          },
        });

        setIsDeleteDialogOpen(false);
      },
      onError: () => {
        toast.error(t('auth.somethingWentWrong'), {
          get description() {
            return t('workspace.weAreUnableToRemoveThisPasskeyAtThe');
          },
        });
      },
    });

  return (
    <div className={cn('flex justify-end space-x-2', className)}>
      <Dialog
        onOpenChange={(value) => !isUpdatingPasskey && setIsUpdateDialogOpen(value)}
        open={isUpdateDialogOpen}
      >
        <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="outline">{t('workspace.edit')}</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.updatePasskey')}</DialogTitle>
            <DialogDescription className="mt-4">
              {t('workspace.youAreCurrentlyUpdatingThe')} <strong>{passkeyName}</strong>{' '}
              {t('workspace.passkey2')}{' '}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(async ({ name }) =>
                updatePasskey({
                  passkeyId,
                  name,
                }),
              )}
            >
              <fieldset className="flex h-full flex-col" disabled={isUpdatingPasskey}>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel required>{t('workspace.name')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter className="mt-4">
                  <DialogClose asChild>
                    <Button type="button" variant="secondary">
                      {t('reports.cancel')}{' '}
                    </Button>
                  </DialogClose>

                  <Button loading={isUpdatingPasskey} type="submit">
                    {t('workspace.update')}{' '}
                  </Button>
                </DialogFooter>
              </fieldset>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        onOpenChange={(value) => !isDeletingPasskey && setIsDeleteDialogOpen(value)}
        open={isDeleteDialogOpen}
      >
        <DialogTrigger asChild={true} onClick={(e) => e.stopPropagation()}>
          <Button variant="destructive">{t('workspace.delete')}</Button>
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspace.deletePasskey')}</DialogTitle>

            <DialogDescription className="mt-4">
              {t('workspace.areYouSureYouWantToRemoveThe')} <strong>{passkeyName}</strong>{' '}
              {t('workspace.passkey3')}{' '}
            </DialogDescription>
          </DialogHeader>

          <fieldset disabled={isDeletingPasskey}>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  {t('reports.cancel')}{' '}
                </Button>
              </DialogClose>

              <Button
                loading={isDeletingPasskey}
                onClick={async () =>
                  deletePasskey({
                    passkeyId,
                  })
                }
                variant="destructive"
              >
                {t('workspace.delete')}{' '}
              </Button>
            </DialogFooter>
          </fieldset>
        </DialogContent>
      </Dialog>
    </div>
  );
};
